import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { SERVICE_LEVEL_PRIORITY_ORDER } from '@provision/sidekick-utils';
import dayjs, { Dayjs } from 'dayjs';

interface EscalationEvaluationContextProps {
  serviceLevel: string;
  onServiceLevelChange: (value: string) => void;
  currentTime: Dayjs;
  onCurrentTimeChange: (value: Dayjs) => void;
  isHoliday?: boolean;
  onHolidayChange?: (value: boolean) => void;
}

const QUICK_SCENARIOS = [
  { label: 'Mon 9am', day: 1, hour: 9, minute: 0 },
  { label: 'Fri 6pm', day: 5, hour: 18, minute: 0 },
  { label: 'Sat 2pm', day: 6, hour: 14, minute: 0 },
] as const;

export const EscalationEvaluationContext = ({
  serviceLevel,
  onServiceLevelChange,
  currentTime,
  onCurrentTimeChange,
  isHoliday,
  onHolidayChange,
}: EscalationEvaluationContextProps) => {
  const dayLabel = currentTime.format('dddd');
  const timeLabel = currentTime.format('h:mm A');

  return (
    <Paper
      variant="outlined"
      sx={{
        px: 2,
        py: 1.5,
        mb: 1.5,
        borderRadius: 1,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          fontWeight: 600,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          fontSize: '0.6875rem',
          color: 'warning.main',
          mb: 0.5,
        }}
      >
        Demo &mdash; Simulating: {dayLabel} {timeLabel}, {serviceLevel} client
        {isHoliday ? ' (Holiday)' : ''}
      </Typography>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mt: 0.5 }}>
        <TextField
          select
          label="Service Level"
          size="small"
          value={serviceLevel}
          onChange={(event) => onServiceLevelChange(event.target.value)}
          sx={{ minWidth: 240 }}
        >
          {SERVICE_LEVEL_PRIORITY_ORDER.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>

        <Box sx={{ minWidth: 260 }}>
          <DateTimePicker
            label="Current Time"
            value={currentTime}
            onChange={(value) => {
              if (value && dayjs(value).isValid()) {
                onCurrentTimeChange(value);
              }
            }}
            slotProps={{ textField: { size: 'small', fullWidth: true } }}
          />
        </Box>

        {onHolidayChange !== undefined ? (
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={isHoliday ?? false}
                onChange={(event) => onHolidayChange(event.target.checked)}
              />
            }
            label="Holiday"
          />
        ) : null}
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
        {QUICK_SCENARIOS.map((scenario) => (
          <Button
            key={scenario.label}
            size="small"
            variant="text"
            sx={{ textTransform: 'none', minWidth: 'auto', px: 1 }}
            onClick={() => {
              const next = dayjs().day(scenario.day).hour(scenario.hour).minute(scenario.minute).second(0);
              onCurrentTimeChange(next);
            }}
          >
            {scenario.label}
          </Button>
        ))}
      </Stack>
    </Paper>
  );
};
